import React from "react";

// Edit Discount on the view-bill page: a finished bill's item discounts and its
// invoice discount are edited in place and saved together. The edits live in
// the form as `discount_edit`:
//   { invoice: { value, isPercentage },
//     items: { [table row key]: { rows, mrpTotal, currentTotal, originalPercentage, value, isPercentage } } }
// `rows` are the stored lines the table row shows (item_id, batch, bin).

const toAmount = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/** A discount, % of or ₹ off a line's MRP total: { discount, percentage, total }. */
export function lineDiscountPreview(mrpTotal, value, isPercentage) {
  const entered = parseFloat(value) || 0;
  const discount = toAmount(isPercentage ? (mrpTotal * entered) / 100 : entered);
  return {
    discount,
    percentage: isPercentage
      ? entered
      : mrpTotal > 0
        ? toAmount((discount / mrpTotal) * 100)
        : 0,
    total: toAmount(mrpTotal - discount),
  };
}

/** Tax inside a tax-inclusive amount at `gstPercent`. */
export const taxIncluded = (amount, gstPercent) =>
  toAmount(amount - (amount * 100) / (100 + (parseFloat(gstPercent) || 0)));

/**
 * The bill total the edits give — the backend works it out the same way when
 * they are saved: Σ item totals less the invoice discount, rounded to the
 * rupee, less coupon / scheme / points.
 */
export function previewBillTotal(values) {
  const edit = values?.discount_edit;
  const benefits =
    (parseFloat(values?.sot_coupon_discount) || 0) +
    (parseFloat(values?.sot_scheme_discount) || 0) +
    (parseFloat(values?.sot_points_discount) || 0);
  const itemsChange = Object.values(edit?.items ?? {}).reduce(
    (change, item) =>
      change +
      lineDiscountPreview(item.mrpTotal, item.value, item.isPercentage).total -
      item.currentTotal,
    0
  );
  const itemsTotal = toAmount(
    (parseFloat(values?.sot_total_amount) || 0) +
      benefits -
      (parseFloat(values?.sot_round_off_value) || 0) +
      (parseFloat(values?.sot_invoice_discount) || 0) +
      itemsChange
  );
  const invoiceValue = parseFloat(edit?.invoice?.value) || 0;
  const invoiceDiscount = toAmount(
    edit?.invoice?.isPercentage ? (itemsTotal * invoiceValue) / 100 : invoiceValue
  );
  return toAmount(Math.round(itemsTotal - invoiceDiscount) - benefits);
}

/** The item edits worth sending: rows whose discount actually changed. */
export function changedItemEdits(items) {
  return Object.values(items ?? {})
    .filter(
      (item) =>
        !(
          item.isPercentage &&
          parseFloat(item.value || 0) === parseFloat(item.originalPercentage || 0)
        )
    )
    .map(({ rows, value, isPercentage }) => ({
      rows,
      value: parseFloat(value) || 0,
      isPercentage,
    }));
}

/** A number field with a %/₹ toggle, for the discount edits. */
export function DiscountValueInput({
  value,
  isPercentage,
  onValueChange,
  onToggle,
  className = "",
}) {
  return (
    <div className={`relative ${className}`}>
      <input
        type="number"
        min="0"
        value={value ?? ""}
        placeholder={isPercentage ? "%" : "₹"}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter must not submit the bill form; keep keys off the page's shortcuts
          e.stopPropagation();
          if (e.key === "Enter") e.preventDefault();
        }}
        className="w-full h-[36px] pl-2 pr-[40px] text-sm border border-[var(--items-search-input-border)] rounded focus:outline-none bg-white"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute top-1/2 -translate-y-1/2 right-0 w-[36px] min-h-[36px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
      >
        {isPercentage ? "%" : "₹"}
      </button>
    </div>
  );
}
